/**
 * LimitIndicator Component - Show usage limits and warnings
 */

import React from 'react';
import { useUserEntitlements } from '../../hooks/useUserEntitlements';
import { useNavigate } from 'react-router-dom';

const LimitIndicator = ({ limitType, compact = false, showUpgradeButton = true }) => {
  const { entitlements, loading } = useUserEntitlements();
  const navigate = useNavigate();

  if (loading || !entitlements) {
    return null;
  }

  // Admin bypass
  if (entitlements.is_admin) {
    return compact ? (
      <span className="text-xs text-gray-500">Unlimited</span>
    ) : (
      <div className="text-sm text-gray-600">
        <span className="font-medium">Unlimited</span> (Admin)
      </div>
    );
  }

  const limit = entitlements.limits?.[limitType];
  if (!limit) return null;

  const isUnlimited = limit.total === -1;
  const percentage = isUnlimited ? 0 : (limit.current_usage / limit.total) * 100;
  const isNearLimit = percentage >= 75;
  const isAtLimit = limit.current_usage >= limit.total && !isUnlimited;

  // Color based on usage
  const getColor = () => {
    if (isAtLimit) return 'red';
    if (isNearLimit) return 'yellow';
    return 'green';
  };

  const color = getColor();
  const colorClasses = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500'
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-700">
          {limit.current_usage} / {isUnlimited ? '∞' : limit.total}
        </span>
        {isAtLimit && (
          <span className="text-xs text-red-600 font-medium">Limit reached</span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 capitalize">
          {limitType.replace(/_/g, ' ')}
        </span>
        <span className="text-sm font-semibold text-gray-900">
          {limit.current_usage} / {isUnlimited ? '∞' : limit.total}
        </span>
      </div>

      {!isUnlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            className={`h-2 rounded-full transition-all ${colorClasses[color]}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}

      {limit.from_tier !== undefined && (
        <div className="text-xs text-gray-500 space-y-1">
          <div>Base: {limit.from_tier === -1 ? 'Unlimited' : limit.from_tier}</div>
          {limit.from_addons > 0 && (
            <div>Add-ons: +{limit.from_addons}</div>
          )}
        </div>
      )}

      {isAtLimit && showUpgradeButton && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={() => navigate('/subscription')}
            className="w-full text-sm bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors"
          >
            Upgrade for More
          </button>
        </div>
      )}

      {isNearLimit && !isAtLimit && (
        <div className="mt-2 text-xs text-yellow-600">
          ⚠️ Approaching limit
        </div>
      )}
    </div>
  );
};

export default LimitIndicator;
